import React, { useMemo } from 'react';
import { Box, CircularProgress, Alert, Typography, Grid } from '@mui/material';
import { formatDateWithDayOfWeek } from '../../utils/dateUtils';
import MonthView from '../visualizations/MonthView';
import TideChart from '../visualizations/TideChart';
import ServiceOpportunityList from '../scheduling/ServiceOpportunityList';
import { ServiceOpportunity } from '../../models';
import { useSearchContext } from '../../contexts/SearchContext';

interface ResultsPageProps {
  // searchParams removed
  isLoading: boolean;
  schedules: Record<string, any>;
  activeSchedules: Record<string, any>;
  currentSchedule: any;
  currentOpportunities: ServiceOpportunity[];
  showOptimized: boolean;
  monthDataIsLoading: boolean;
  currentSelectedDay: string | null;
  selectedOpportunities: ServiceOpportunity[];
  selectedOpportunityIds: Set<string>;
  formattedToday: string;
  handleSelectDate: (date: string) => void;
  handleMonthChange: (direction: 'prev' | 'next') => void;
  toggleOpportunitySelection: (opportunity: ServiceOpportunity) => void;
  tideHeightStatistics?: { minHeight: number; maxHeight: number }; // Add tide height statistics
}

const ResultsPage: React.FC<ResultsPageProps> = ({
  isLoading,
  schedules,
  activeSchedules,
  currentSchedule,
  currentOpportunities,
  showOptimized,
  monthDataIsLoading,
  currentSelectedDay,
  selectedOpportunities,
  selectedOpportunityIds,
  formattedToday,
  handleSelectDate,
  handleMonthChange,
  toggleOpportunitySelection,
  tideHeightStatistics
}) => {
  // Get search parameters from context
  const { searchParams } = useSearchContext();
  // Extract services from searchParams, provide default empty array
  const services = searchParams?.services ?? [];

  // Prepare daily tide data (including astronomical and cycle type) for MonthView
  const dailyTideDataByDate = useMemo(() => {
    return Object.fromEntries(
      Object.entries(schedules).map(([date, data]) => [
        date,
        data.tideData // Pass the whole tideData object
      ])
    );
  }, [schedules]);

  if (isLoading && Object.keys(schedules).length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Enhanced guard condition - if no schedules or no services, don't render any results
  if (Object.keys(schedules).length === 0 || !searchParams || !searchParams.services || searchParams.services.length === 0) {
    return <Alert severity="info">No results to display. Please perform a search first.</Alert>;
  }
  
  // Additional defensive check for the services array
  const serviceDefinitions = searchParams.services || [];
  if (serviceDefinitions.length === 0) {
    return <Alert severity="info">Missing service definitions. Please start a new search.</Alert>;
  }
  
  return (
    <Box>
     
      {(currentSelectedDay || isLoading) && (
        <Grid container spacing={1}>
          {/* Monthly view on the left */}
          <Grid item xs={12} md={5}>
            <MonthView
              dates={Object.keys(schedules)}
              selectedDate={currentSelectedDay || formattedToday}
              tideDataByDate={Object.fromEntries(
                Object.entries(schedules).map(([date, data]) => [
                  date,
                  data.tideData.times.map((time: string, index: number) => ({
                    time,
                    height: data.tideData.heights[index]
                  }))
                ])
              )}
              opportunitiesByDate={Object.fromEntries(
                Object.entries(activeSchedules).map(([date, data]) => [
                  date,
                  data ? data.opportunities : schedules[date].opportunities
                ])
              )}
              onDateSelect={handleSelectDate}
              onMonthChange={handleMonthChange}
              showDetailed={!showOptimized}
              isLoading={monthDataIsLoading}
              serviceDefinitions={services}
              dailyTideDataByDate={dailyTideDataByDate} // Pass the new prop
            />
          </Grid>
          
          {/* Daily view on the right */}
          <Grid item xs={12} md={7}>
            <Typography 
              variant="h5" 
              align="center" 
              sx={{ 
                fontWeight: 'medium', 
                my: 2,
                color: 'text.primary' 
              }}
            >
              {formatDateWithDayOfWeek(currentSelectedDay || formattedToday)}
            </Typography>
            {currentSchedule && (
              <TideChart
                tideData={currentSchedule.tideData.times.map((time: string, index: number) => ({
                  time,
                  height: currentSchedule.tideData.heights[index]
                }))}
                serviceOpportunities={selectedOpportunities}
                date={currentSelectedDay || formattedToday}
                services={services}
                astronomicalData={currentSchedule.tideData.astronomicalData} // Get directly from currentSchedule
                monthlyTideStatistics={tideHeightStatistics} // Pass the tide height statistics
                key={`tide-chart-${currentSelectedDay || formattedToday}`}
              />
            )}
            
            {currentSchedule && (
              <ServiceOpportunityList
                opportunities={currentOpportunities}
                onToggleOpportunity={toggleOpportunitySelection}
                selectedOpportunityIds={selectedOpportunityIds}
                services={services}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ResultsPage;
